import { useMemo, useRef, useState } from "react";
import date from "date-and-time";
import { RowModel, Table, createColumnHelper, CellContext, SortingState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";

import PagedTable from "../shared/Tables/PagedTable";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import LoadingWheel from "../shared/LoadingWheel";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import Modal from "../../layouts/Modal";
import PinProtect, { PinProtectState } from "../shared/PinProtect";
import { ButtonInput } from "../shared/Inputs/ButtonInput";

import usePayments from "../../data/usePayments";
import usePaymentStatus from "../../data/usePaymentStatus";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useAuth } from "../../contexts/AuthContext";

import ContactFromId from "../contacts/ContactFromId";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";

import CarCollectedImage from "./CarCollectedImage";
import PaymentDetails from "./PaymentDetails";
import PaymentStatusCell from "./PaymentStatusCell";
import PaymentMethodCell from "./PaymentMethodCell";
import LeadStatusCell from "../leads/LeadTable/TableSubComponents/LeadStatusCell";
import VehicleVatStatusCell from "../vehicles/VehicleVatStatusCell";
import usePricingAccess from "../../hooks/usePricingAccess";

import { ILead, PaymentStatus } from "../../models/Lead";

function PaymentMessageCell({ info }: { info: CellContext<ILead, string | null> }) {
  const { paymentStatus: getPaymentStatus } = usePaymentStatus(
    info.row.original.id,
    info.row.original.tellerooTransactionId
  );

  const statusMessage = useMemo(() => {
    const status =
      info.row.original.paymentStatus === PaymentStatus.Approved
        ? getPaymentStatus.isLoading
          ? ""
          : getPaymentStatus.data?.data?.message
        : info.getValue();

    return status;
  }, [info, getPaymentStatus.isLoading, getPaymentStatus.data]);

  return (
    <div className="flex justify-center items-center flex-1 p-4">
      <pre className="whitespace-pre-line">{statusMessage}</pre>
    </div>
  );
}

export default function PagedPaymentTable({
  additionalBody,
}: {
  additionalBody: Record<string, any>;
}) {
  const queryClient = useQueryClient();
  const { approvePayments, cancelPayments, setPaymentStatus } = usePayments();
  const { saveHandlers } = useDefaultCRUDHandlers("Leads");
  const { claims } = useAuth();
  const { canViewPrices } = usePricingAccess();

  const tableRef = useRef<Table<ILead> | null>(null);
  const [selectedLeads, setSelectedLeads] = useState<ILead[]>([]);

  const [approveOpen, setApproveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [updatePaymentOpen, setUpdatePaymentOpen] = useState(false);

  const [statusUpdate, setStatusUpdate] = useState<number>(0);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinState, setPinState] = useState<PinProtectState>();

  const [isApprovingLoading, setIsApprovingLoading] = useState(false);
  const [isCancellingLoading, setIsCancellingLoading] = useState(false);
  const [isUpdatingLoading, setIsUpdatingLoading] = useState(false);

  const colHelper = createColumnHelper<ILead>();

  const cols = useMemo(
    () => [
      TableCheckboxCol(false),

      // Use accessorFn + id so server sorting uses id (not accessorKey) :contentReference[oaicite:4]{index=4}
      colHelper.accessor((row) => row.contact?.fullname ?? "", {
        id: "Contact.Fullname",
        header: "Name",
        sortingFn: "auto",
      }),

      colHelper.display({
        id: "Price",
        header: "Price",
        cell: (info) => (
          <span>
            {PriceFormat(info.row.original.priceWithVAT ?? info.row.original.price)}{" "}
            {info.row.original.applyVAT && "(incl. VAT)"}
          </span>
        ),
      }),

      // Vehicle is the purchase-VAT source of truth; row.applyVAT is only the lead mirror.
      ...(canViewPrices
        ? [
          colHelper.display({
            id: "vatStatus",
            header: "VAT",
            cell: (info) => (
              <VehicleVatStatusCell
                applyVAT={info.row.original.vehicle?.applyVAT}
                vatSalesTreatment={info.row.original.vehicle?.vatSalesTreatment}
              />
            ),
          }),
        ]
        : []),

      colHelper.accessor((row) => row.commission ?? 0, {
        id: "Commission",
        header: "Commission",
        cell: (info) => <span>{PriceFormat(info.getValue())}</span>,
      }),

      colHelper.display({
        id: "VRM",
        header: "VRM",
        cell: (info) => <span>{info.row.original.vehicle?.vrm ?? ""}</span>,
        enableSorting: false,
      }),

      colHelper.accessor((row) => row.status, {
        id: "Status",
        header: "Lead",
        enableSorting: false,
        cell: (info) => <LeadStatusCell status={info.getValue()} />,
      }),

      colHelper.display({
        id: "image",
        header: "Car Collected",
        cell: (info) => <CarCollectedImage lead={info.row.original} />,
      }),

      colHelper.display({
        id: "paymentDetails",
        header: "Details",
        cell: (info) => <PaymentDetails lead={info.row.original} />,
      }),

      colHelper.accessor((row) => row.paymentMethod, {
        id: "PaymentMethod",
        header: "Method",
        cell: (info) => <PaymentMethodCell paymentMethod={info.getValue()} />,
      }),

      colHelper.accessor((row) => row.paymentStatus, {
        id: "PaymentStatus",
        header: "Status",
        cell: (info) => <PaymentStatusCell lead={info.row.original} />,
      }),

      colHelper.accessor((row) => row.paymentMessage, {
        id: "paymentMessage",
        header: "Message",
        enableSorting: false,
        cell: (info) => <PaymentMessageCell info={info} />,
      }),

      colHelper.accessor((row) => row.vehicle?.collectedOn, {
        id: "Vehicle.CollectedOn",
        header: "Collected Date",
        sortingFn: "auto",
        cell: (info) => (
          <span>
            {info.getValue()
              ? date.format(new Date(info.getValue() as any), "DD/MM/YYYY")
              : "Not Collected"}
          </span>
        ),
      }),

      colHelper.accessor((row) => row.paymentRaisedBy, {
        id: "paymentRaisedBy",
        header: "Raised By",
        enableSorting: false,
        cell: (info) => <ContactFromId contactId={info.getValue()!} />,
      }),

      colHelper.accessor((row) => row.paymentApprovedBy, {
        id: "paymentApprovedBy",
        header: "Approved By",
        enableSorting: false,
        cell: (info) => <ContactFromId contactId={info.getValue()!} />,
      }),
    ],
    [canViewPrices]
  );

  const initialSort: SortingState = useMemo(() => [{ id: "Vehicle.CollectedOn", desc: false }], []);

  const stashSelection = (rows: RowModel<ILead>, table: Table<ILead>) => {
    tableRef.current = table;
    setSelectedLeads(rows.rows.map((r) => r.original));
  };

  const refetchPaged = () => {
    // Matches PagedTable query key prefix: ["pagedTable", tableConfig.url, ...] :contentReference[oaicite:5]{index=5}
    queryClient.invalidateQueries({ queryKey: ["pagedTable", "/lead/payments/filtered"] });
  };

  const handleApprovePayments = () => {
    const leadIds = selectedLeads.map((l) => l.id);
    if (leadIds.length === 0) return;

    setIsApprovingLoading(true);

    approvePayments.mutate(
      { leads: leadIds },
      {
        onSuccess: () => {
          tableRef.current?.resetRowSelection();
          saveHandlers.onSuccess();
          setApproveOpen(false);
          setSelectedLeads([]);
          refetchPaged();
        },
        onError: saveHandlers.onError,
        onSettled: () => setIsApprovingLoading(false),
      }
    );
  };

  const handleCancelPayments = () => {
    const leadIds = selectedLeads.map((l) => l.id);
    if (leadIds.length === 0) return;

    setIsCancellingLoading(true);

    cancelPayments.mutate(
      { leads: leadIds },
      {
        onSuccess: () => {
          tableRef.current?.resetRowSelection();
          saveHandlers.onSuccess();
          setCancelOpen(false);
          setSelectedLeads([]);
          refetchPaged();
        },
        onError: saveHandlers.onError,
        onSettled: () => setIsCancellingLoading(false),
      }
    );
  };

  const openPaymentUpdate = (rows: RowModel<ILead>, table: Table<ILead>) => {
    stashSelection(rows, table);
    setUpdatePaymentOpen(true);
  };

  const handlePaymentUpdate = () => {
    setPinModalOpen(true);
    setPinState({
      pinLabel: "Submit Payment Status",
      onPinSuccess: async () => {
        setIsUpdatingLoading(true);

        try {
          for (const lead of selectedLeads) {
            // prefer mutateAsync if you have it; otherwise mutate in loop is OK
            // @ts-ignore
            if (setPaymentStatus.mutateAsync) {
              // @ts-ignore
              await setPaymentStatus.mutateAsync({
                status: statusUpdate,
                leadId: lead.id,
                transactionId: lead.tellerooTransactionId,
              });
            } else {
              setPaymentStatus.mutate({
                status: statusUpdate,
                leadId: lead.id,
                transactionId: lead.tellerooTransactionId,
              });
            }
          }

          tableRef.current?.resetRowSelection();
          saveHandlers.onSuccess();
          setUpdatePaymentOpen(false);
          setSelectedLeads([]);
          refetchPaged();
        } catch (e) {
          saveHandlers.onError();
        } finally {
          setIsUpdatingLoading(false);
          setPinModalOpen(false);
        }
      },
      onPinFailed: () => {},
    });
  };

  const isAdmin = claims?.isAdmin === true;

  return (
    <>
      <PinProtect open={pinModalOpen} setOpen={setPinModalOpen} pinState={pinState} />

      <Modal open={updatePaymentOpen} setOpen={setUpdatePaymentOpen} width="max-w-3xl">
        <div className="space-y-4">
          <p>You are about to update {selectedLeads.length} statuses!</p>
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-2">
            <select
              value={statusUpdate}
              className="border p-1"
              onChange={(e) => setStatusUpdate(parseInt(e.target.value))}
            >
              {Object.values(PaymentStatus)
                .filter((ct) => isNaN(Number(ct)))
                .map((status, i) => (
                  <option key={status} value={i}>
                    {status}
                  </option>
                ))}
            </select>

            <ButtonInput
              label={isUpdatingLoading ? "Updating..." : "Update Status"}
              classes=""
              onClick={handlePaymentUpdate}
            />
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={approveOpen}
        setOpen={setApproveOpen}
        title="Approve Payments?"
        confirmButtonText="Approve"
        message={`Are you sure you would like to approve these ${selectedLeads.length} payments?\n\nThis will create a payrun in Telleroo that you will need to approve.`}
        isLoading={isApprovingLoading}
        onConfirm={handleApprovePayments}
      />

      <ConfirmModal
        open={cancelOpen}
        setOpen={setCancelOpen}
        title="Cancel Payments?"
        confirmButtonText="Cancel Payments"
        message={`Are you sure you would like to cancel these ${selectedLeads.length} payments?\n\nThis will remove them from this table and will move the payment status back to not started.\n\nThis will NOT work for payments that have already been sent to Telleroo, they need to be cancelled manually.`}
        isLoading={isCancellingLoading}
        onConfirm={handleCancelPayments}
      />

      <PagedTable<ILead>
        url="/payment/filtered"
        columns={cols}
        queryLabel={undefined} // we use the page-level search UI (writes ?search=...)
        showTotal={true}
        initialSort={initialSort}
        additionalBody={additionalBody}
        onRowClick={(lead: ILead) => openInNewTab(`leads/${lead.id}`)}
        actionButtons={
          !isAdmin ? [] : [
            {
              label: "Approve Payments",
              disabledOnNoRows: true,
              isLoading: isApprovingLoading,
              onClick: (rows, table) => {
                stashSelection(rows, table);
                setApproveOpen(true);
              },
            },
            {
              label: "Change Payment Status",
              disabledOnNoRows: true,
              isLoading: isUpdatingLoading,
              onClick: openPaymentUpdate,
            },
            {
              label: "Cancel Raised Payments",
              disabledOnNoRows: true,
              isLoading: isCancellingLoading,
              onClick: (rows, table) => {
                stashSelection(rows, table);
                setCancelOpen(true);
              },
            },
          ]
        }
      />
    </>
  );
}